// The server's regions (VisionPlatform regions §5.2, contract regions 0.2.0), read from the app's own origin for the
// street map of the organisation's region (map-tiles MAP-07, MAP-10, MAP-11; web map-tiles WEB-MAPT-01 … -03).
import { queryOptions } from "@tanstack/react-query";
import type { Region } from "./generated/regions";

export type { Region };

/** Same-origin, like servers.json, so the CSP's connect-src stays as it is. */
export const REGIONS_PATH = "/.well-known/creavision/regions.json";

export type Theme = "light" | "dark";
/** A style in the region's `maps` folder: the street map by theme, or the satellite layer (regions 0.2.1). */
export type StyleName = Theme | "satellite";

/** What the map needs of a region: its id, status and the folder of its styles. */
export type MapRegion = Pick<Region, "id" | "status" | "maps">;

/** The regions of a regions.json document; an entry without an id or status is left out, never guessed. */
export function parseRegions(raw: unknown): MapRegion[] {
  if (typeof raw !== "object" || raw === null) throw new Error("regions.json is not an object");
  const list = (raw as { regions?: unknown }).regions;
  if (!Array.isArray(list)) throw new Error("regions.json has no regions");
  return list.flatMap((r: unknown): MapRegion[] => {
    if (typeof r !== "object" || r === null) return [];
    const o = r as Record<string, unknown>;
    if (typeof o.id !== "string" || !["open", "closed", "retiring"].includes(o.status as string)) return [];
    return [
      {
        id: o.id,
        status: o.status as Region["status"],
        ...(typeof o.maps === "string" ? { maps: o.maps } : {}),
      },
    ];
  });
}

/**
 * The organisation's region: the one its `data_region` names; without one, the server's one open region (SRV-08).
 * No region when the id is unknown or several are open: the map then has no streets rather than another region's.
 */
export function regionFor(regions: readonly MapRegion[], dataRegion: string | undefined): MapRegion | undefined {
  if (dataRegion) return regions.find((r) => r.id === dataRegion);
  const open = regions.filter((r) => r.status === "open");
  return open.length === 1 ? open[0] : undefined;
}

/**
 * The style to load (MAP-10): `<maps>light.json` or `<maps>dark.json`, or `<maps>satellite.json` (maps-2 MAP2-01). Only an HTTPS folder on this app's own
 * origin, ending in `/`, is used: the CSP allows no other host (MAP-06), and the apps never fall back to a
 * third-party tile server (MAP-07).
 */
export function styleUrl(region: MapRegion | undefined, origin: string, theme: StyleName): string | undefined {
  const maps = region?.maps;
  if (!maps || !maps.endsWith("/")) return undefined;
  let url: URL;
  try {
    url = new URL(maps);
  } catch {
    return undefined;
  }
  // The contract asks for https; plain http only for a build on this machine (the e2e suite's localhost).
  const local = url.hostname === "localhost" || url.hostname === "127.0.0.1";
  if (url.protocol !== "https:" && !(url.protocol === "http:" && local)) return undefined;
  if (url.origin !== origin || url.username || url.password) return undefined;
  return `${maps}${theme}.json`;
}

export const regionsQuery = () =>
  queryOptions({
    queryKey: ["regions.json"],
    queryFn: async () => {
      const res = await fetch(REGIONS_PATH, { cache: "no-cache" });
      if (!res.ok) throw new Error(`regions.json: HTTP ${res.status}`);
      return parseRegions(await res.json());
    },
    staleTime: 60 * 60_000,
    retry: 1,
  });
